<template>
  <v-container
    fluid
    class="pa-6">
    <!-- Page Title -->
    <div class="text-h5 font-weight-bold mb-1">
      จัดการ Time Slot
    </div>
    <div class="body-2 grey--text mb-5">
      กำหนดช่วงเวลาที่เปิดรับการจัดส่ง
    </div>

    <v-row>
      <!-- LEFT: Slot List -->
      <v-col
        cols="12"
        md="7">
        <v-card
          rounded="lg"
          elevation="1"
          class="pa-4">
          <div class="d-flex align-center mb-4">
            <span class="subtitle-1 font-weight-bold">รายการ Slot ทั้งหมด</span>
          </div>

          <v-progress-linear
            v-if="loading"
            indeterminate
            color="secondary"
            class="mb-3" />

          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center caption grey--text">
                    เวลาเริ่ม
                  </th>
                  <th class="text-center caption grey--text">
                    เวลาสิ้นสุด
                  </th>
                  <th class="text-center caption grey--text">
                    จัดการ
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="slot in slots"
                  :key="slot.id">
                  <td class="body-2 font-weight-medium text-center">
                    {{ slot.startTime }}
                  </td>
                  <td class="body-2 text-center">
                    {{ slot.endTime }}
                  </td>
                  <td class="text-center">
                    <v-btn
                      icon
                      color="primary"
                      class="mr-1"
                      @click="openEdit(slot)">
                      <v-icon>
                        mdi-pencil
                      </v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      color="red"
                      :loading="deletingId === slot.id"
                      @click="confirmDelete(slot)">
                      <v-icon>
                        mdi-delete
                      </v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr v-if="!loading && slots.length === 0">
                  <td
                    colspan="4"
                    class="text-center caption grey--text py-6">
                    ไม่มี Slot
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>

      <!-- RIGHT: Form -->
      <v-col
        cols="12"
        md="5">
        <v-card
          rounded="lg"
          elevation="1"
          style="overflow:hidden;">
          <v-toolbar
            :color="isEditing ? 'primary' : 'secondary'"
            dark
            flat
            dense>
            <v-icon
              small
              class="mr-2">
              mdi-clock-outline
            </v-icon>
            <span class="subtitle-2">{{ isEditing ? 'แก้ไข Slot' : 'เพิ่ม Slot ใหม่' }}</span>
          </v-toolbar>

          <div class="pa-4">
            <!-- Start Time -->
            <div class="caption grey--text mb-1">
              เวลาเริ่ม
            </div>
            <v-text-field
              v-model="form.startTime"
              type="time"
              outlined
              dense
              hide-details
              class="mb-4"
              append-icon="mdi-clock-outline"
              :error="!!errors.startTime"
              :error-messages="errors.startTime" />

            <!-- End Time -->
            <div class="caption grey--text mb-1">
              เวลาสิ้นสุด
            </div>
            <v-text-field
              v-model="form.endTime"
              type="time"
              outlined
              dense
              hide-details
              class="mb-4"
              append-icon="mdi-clock-outline"
              :error="!!errors.endTime"
              :error-messages="errors.endTime" />

            <!-- Actions -->
            <v-btn
              block
              color="secondary"
              large
              rounded
              elevation="0"
              class="mb-3"
              :loading="saving"
              :disabled="saving"
              @click="confirmSave">
              <v-icon
                left
                small>
                mdi-content-save-outline
              </v-icon>
              {{ isEditing ? 'บันทึกการแก้ไข' : 'เพิ่ม Slot' }}
            </v-btn>
            <v-btn
              block
              large
              rounded
              outlined
              elevation="0"
              :disabled="saving"
              @click="resetForm">
              ยกเลิก
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import DeliveryBookingProvider from '@/resources/DeliveryBookingProvider'
import { mapActions } from 'vuex'
import { sortByTime } from '@/assets/js/DateTimeUtils'

export default {
  name: 'BookingSlots',

  data () {
    return {
      deliveryBookingService: new DeliveryBookingProvider(),

      slots: [],
      loading: false,
      saving: false,
      deletingId: null,
      deletingSlot: null,

      isEditing: false,
      editingId: null,

      form: {
        startTime: '',
        endTime: '',
        status: 'active'
      },

      errors: {
        startTime: '',
        endTime: ''
      }
    }
  },

  async mounted () {
    await this.fetchSlots()
  },

  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal'
    }),

    // ─── API ─────────────────────────────────────────────────

    async fetchSlots () {
      this.loading = true
      try {
        const { data } = await this.deliveryBookingService.getSlots()
        this.slots = sortByTime([...data], 'startTime')
      } catch (e) {
        this.setSnackbar({
          value: true,
          message: 'โหลด Slot ไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async save () {
      this.saving = true
      try {
        const payload = {
          startTime: this.form.startTime,
          endTime: this.form.endTime,
          ...(this.isEditing && { status: this.form.status })
        }

        if (this.isEditing) {
          await this.deliveryBookingService.updateSlot(this.editingId, payload)
        } else {
          await this.deliveryBookingService.createSlot(payload)
        }

        this.setSnackbar({
          value: true,
          message: 'แก้ไข Slot เรียบร้อยแล้ว',
          type: 'success'
        })

        await this.fetchSlots()
        this.resetForm()
      } catch (e) {
        this.setSnackbar({
          value: true,
          message: e?.message || 'บันทึกไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    },

    async deleteSlot () {
      if (!this.deletingSlot) return
      this.deletingId = this.deletingSlot.id
      try {
        await this.deliveryBookingService.deleteSlot(this.deletingSlot.id)
        this.setSnackbar({
          value: true,
          message: 'ลบ Slot เรียบร้อยแล้ว',
          type: 'success'
        })
        await this.fetchSlots()
        this.deletingSlot = null
      } catch (e) {
        this.setSnackbar({
          value: true,
          message: 'ลบ Slot ไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.deletingId = null
      }
    },

    // ─── Form ─────────────────────────────────────────────────

    openCreate () {
      this.resetForm()
    },

    openEdit (slot) {
      this.isEditing = true
      this.editingId = slot.id
      this.form = {
        startTime: slot.startTime,
        endTime: slot.endTime,
        status: slot.status
      }
      this.errors = { startTime: '', endTime: '' }
    },

    confirmDelete (slot) {
      this.deletingSlot = slot
      this.setModal({
        value: true,
        title: 'ยืนยันการลบ',
        message: `
          ต้องการลบ Slot ${this.deletingSlot && this.deletingSlot.startTime } - ${this.deletingSlot && this.deletingSlot.endTime } ใช่หรือไม่?
        `,
        confirmText: 'ลบ',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'ยกเลิก',
        onConfirm: () => this.deleteSlot()
      })
    },

    confirmSave () {
      if (!this.validate()) return
      this.setModal({
        value: true,
        title: 'ยืนยันการบันทึก',
        message: 'ต้องการบันทึกใช่หรือไม่',
        confirmText: 'บันทึก',
        confirmType: 'success',
        cancelType: '',
        cancelText: 'ยกเลิก',
        onConfirm: () => this.save()
      })
    },

    resetForm () {
      this.isEditing = false
      this.editingId = null
      this.form = { startTime: '', endTime: '', status: 'active' }
      this.errors = { startTime: '', endTime: '' }
    },

    validate () {
      this.errors = { startTime: '', endTime: '' }
      let valid = true
      if (!this.form.startTime) {
        this.errors.startTime = 'กรุณาระบุเวลาเริ่ม'
        valid = false
      }
      if (!this.form.endTime) {
        this.errors.endTime = 'กรุณาระบุเวลาสิ้นสุด'
        valid = false
      }
      if (this.form.startTime && this.form.endTime && this.form.startTime >= this.form.endTime) {
        this.errors.endTime = 'เวลาสิ้นสุดต้องมากกว่าเวลาเริ่ม'
        valid = false
      }
      return valid
    }
  }
}
</script>
